<template>
  <div class="chart-component__main">
    <template v-if="settings.titleVisible">
      <div class="chart__header">
        <h3 class="chart__title" @click="$emit('click:title', $event)" :style="{ fontSize: (fontSize + 4) + 'px' }">
          <editable-text
            :editable="editable"
            :text="title ?? ''"
            :displayed-text="titleAsText"
            @update:text="emit('update:title', $event)"
          >
            <span v-if="title">{{ title }}</span>
            <span v-else v-for="part in formattedTitle" :key="part.text" :style="part.style || ''">
              {{ part.text }}
            </span>
          </editable-text>
        </h3>
      </div>
    </template>
    <template v-if="collectedChartData.data.length == 0">
      <slot name="empty" :icon="chartConfig?.icon">
        <div class="chart-component__empty" :style="{ height: chartHeight + 'px', width: width + 'px' }">
          <slot name="empty-icon" :icon="chartConfig?.icon">
            <lucide-icon v-if="chartConfig" :name="chartConfig.icon" />
          </slot>
          <div v-if="selectedItems.length == 0">
            {{ $t("messages.noDataSelected") }}
          </div>
          <div v-else-if="selectedSimulations.length == 0">
            <template v-if="(chartItem.data.simulation || []).includes(-1)">
              {{ $t("messages.noSimulationOpen") }}
            </template>
            <template v-else>
              {{ $t("messages.noSimulationSelected") }}
            </template>
          </div>
          <div v-else>
            {{ $t("errors.noChartData") }}
          </div>
        </div>
      </slot>
    </template>
    <template v-else-if="chartConfig && width > 0 && chartHeight > 0">
      <component
        :is="chartConfig.component"
        :key="'chart-component-' + chartItem.uid"
        ref="chartRef"
        class="chart-component__chart chart"
        :class="{
          '-fitting': settings.width == null && settings.height == null,
          '-small':
            width < 400 ||
            chartHeight < 400
        }"
        :data="collectedChartData.data"
        :column-data="collectedChartData.columns"
        :settings="{ ...settings, titleVisible: false }"
        :width="width"
        :height="chartHeight"
        :title-clickable="false"
        :margin="margin"
        @hover-on="updateHoverLegend($event)"
        @hover-off="updateHoverLegend(null)"
        @open-round-actions="emit('open-round-actions', $event)"
        @click:title="$emit('click:title', $event)"
        @click:column-title="emit('click:column-title', $event)"
        @click:axis-title="emit('click:axis-title', $event)"
      />

      <chart-hover-legend
        v-if="hoverLegend.item"
        :x="hoverLegend.x"
        :y="hoverLegend.y"
        :item="hoverLegend.item"
        :parent-item="hoverLegend.parentItem"
        :y-axis="settings.yAxis"
      />


      <template v-if="withLegend">
        <button
          class="button-icon evaluation-chart__legendbutton"
          type="button"
          :title="$t('labels.showDiagramLegend')"
          @click="toggleLegend"
        >
          <lucide-icon name="file-text" />
        </button>

        <chart-legend-popup
          v-if="legend.visible"
          :id="chartItem.uid"
          :legend="collectedChartData.legend"
          :settings="settings"
          :x="legend.x"
          :y="legend.y"
          @close="legend.visible = false"
          @update:short-title="emit('update:short-title', $event)"
        />
      </template>

      <slot name="legend" :legend-data="collectedChartData.legend" :settings="settings"></slot>

      <slot></slot>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { getElementOrAncestorWithTag } from '@/helpers/utility/DOM';

import { useSettingsStore } from '@editor/store/settings.store';

import { useChartData, type ChartItemOptions } from './useChartData';
import { mapChartData } from './chartDataMapper';
import { useChartTitle } from './useChartTitle';

import LucideIcon from '@core/ui/LucideIcon.vue';
import ChartHoverLegend from '@editor/items/charts/ChartHoverLegend.vue';
import ChartLegendPopup from './ChartLegendPopup.vue';
import EditableText from '@core/input/EditableText.vue';

import type { DataEntry, EntryValue } from './ChartData';
import type { ChartEmitEvents } from './useChartEngine';
import type { PreSettings } from './charts';
import { useSceneTree } from '../scene/sceneTree';
import { useElements } from '../element/elements';
import { useEvaluationStore } from '../evaluation/evaluations.store';
import { useSimulationViewStore } from '@editor/views/simulation/simulationView.store';
import { useRelationStore } from '../relation/relations.store';

const props = withDefaults(defineProps<{
  chartItem: ChartItemOptions;
  editable?: boolean;
  title?: string | null;
  width: number;
  height: number;
  hoverable?: boolean;
  titleClickable?: boolean;
  margin?: { top: number; left: number; right: number; bottom: number };
  withLegend?: boolean;
  overrideSettings?: Partial<PreSettings>;
}>(), {
  editable: false,
  titleClickable: false,
  hoverable: true,
  withLegend: false,
  margin: () => ({ top: 10, left: 0, right: 10, bottom: 10 })
});

const emit = defineEmits<ChartEmitEvents & { 
  (e: "update:short-title", value: {
    uid: string;
    title: string;
    shortTitle?: string | undefined;
    type: string;
    id: string | number;
  }): void,
  (e: "update:title", value: string | null): void,
}>();

const { t } = useI18n();
const chartOptions = computed((): ChartItemOptions => ({
  uid: props.chartItem?.uid,
  data: props.chartItem?.data,
  dataToggles: props.chartItem?.dataToggles,
  itemConfigs: props.chartItem?.itemConfigs,
  chartType: props.chartItem?.chartType,
  chartSettings: props.chartItem?.chartSettings,
}));
const {
  preSettings,
  availableSettingsValues,
  chartConfig,
  chartAxisConfig,
  itemConfigs,
  dataToggles,
  selectedItem,
  selectedItems,
  selectedSimulation,
  selectedSimulations,
  simulationRounds,
  playedRoundsInRange,
  roundsInRange,
  commonRoundSettings,
  orderedTargets,
  orderedResources,
  orderedIndices,
  availableItems,
  allAvailableTargets,
  allAvailableResources,
  allAvailableIndices,
  allTargetsOrder,
  allResourcesOrder,
  allIndicesOrder,
  actionActivations,
  incidentActivations,
  scheduledEffectActivations,
} = useChartData(chartOptions);


// ==============
// SETTINGS

const scaleColors = computed(() => {
  const colors = useSettingsStore().targetValueColors.slice();
  if (colors == null) return [];
  return colors;
});

const settings = computed(() => {
  return {
    ...availableSettingsValues.value,
    scaleColors: scaleColors.value,
    ...preSettings.value,
    ...props.overrideSettings,
  };
});


// ====================
// CHART DATA

const chartUid = computed(() => props.chartItem?.uid);
const chartType = computed(() => props.chartItem?.chartType ?? "bar");
const elementColors = computed(() => useSceneTree().elementColors);
const elementUnits = computed(() => useElements().units);
const titleAbbreviations = computed(() => useEvaluationStore().titleAbbreviations);
const activeSimulationId = computed(() => useSimulationViewStore().simulation?.id ?? null);
const outgoingRelationsByElement = computed(() => useRelationStore().activeByStartUid);

/**
 * Chart data for the current chart.
 */
const collectedChartData = computed(() => {
  const config = chartConfig.value;
  const settings = availableSettingsValues.value;
  if (!config) return { data: [], legend: {} };
  // console.log("recompute chart data");
  const chartData = mapChartData(
    {
      x: settings.xAxis,
      y: settings.yAxis
    },
    {
      chartUid: chartUid.value,
      chartType: chartType.value,
      itemConfigs: itemConfigs.value,
      dataToggles: dataToggles.value,

      selectedItem: selectedItem.value,
      selectedItems: selectedItems.value,
      selectedSimulation: selectedSimulation.value,
      selectedSimulations: selectedSimulations.value,

      availableSettingsValues: availableSettingsValues.value,
      chartAxisConfig: chartAxisConfig.value,
      commonRoundSettings: commonRoundSettings.value,

      simulationRounds: simulationRounds.value,
      playedRoundsInRange: playedRoundsInRange.value,
      roundsInRange: roundsInRange.value,

      orderedTargets: orderedTargets.value,
      orderedResources: orderedResources.value,
      orderedIndices: orderedIndices.value,
      availableItems: availableItems.value,
      allAvailableTargets: allAvailableTargets.value,
      allAvailableResources: allAvailableResources.value,
      allAvailableIndices: allAvailableIndices.value,
      allTargetsOrder: allTargetsOrder.value,
      allResourcesOrder: allResourcesOrder.value,
      allIndicesOrder: allIndicesOrder.value,

      actionActivations: actionActivations.value,
      incidentActivations: incidentActivations.value,
      scheduledEffectActivations: scheduledEffectActivations.value,

      elementColors: elementColors.value,
      elementUnits: elementUnits.value,
      outgoingRelationsByElement: outgoingRelationsByElement.value,
      titleAbbreviations: titleAbbreviations.value,

      activeSimulationId: activeSimulationId.value,
    },
    t
  );
  return chartData;
});


// ================
// TITLE


const fontSize = computed(() => {
  return settings.value.fontSize ?? 12;
});

const titleVisible = computed(() => {
  return settings.value.titleVisible ?? true;
});

const { formattedTitle, title: titleAsText } = useChartTitle(() => props.title, () => ({
  singleSimulation: chartAxisConfig.value?.singleSimulation,
  selectedSimulations: selectedSimulations.value,
  selectedItems: selectedItems.value,
  availableItems: availableItems.value,
  dataToggles: dataToggles.value
}));

const titleHeight = computed(() => {
  if (!titleVisible.value) return 0;
  return fontSize.value * 3;
});

const chartHeight = computed(() => {
  return props.height - titleHeight.value;
});

// ==============
// HOVER LEGEND

const hoverLegend = reactive({
  item: null as null | EntryValue,
  parentItem: undefined as undefined | DataEntry,
  key: "",
  x: 0,
  y: 0
});

function updateHoverLegend(
  content: {
    position: { x: number; y: number };
    item: EntryValue;
    parentItem?: DataEntry;
  } | null
) {
  if (!props.hoverable) return;
  if (content == null) {
    hoverLegend.item = null;
    hoverLegend.key = "#none";
    return;
  }
  if (content.item?.key == hoverLegend?.key) {
    hoverLegend.x = content.position.x;
    hoverLegend.y = content.position.y;
  } else {
    hoverLegend.key = content.item.key;
    hoverLegend.item = content.item;
    hoverLegend.parentItem = content.parentItem;
    hoverLegend.x = content.position.x;
    hoverLegend.y = content.position.y;
  }
}



// ==============
// LEGEND

const legend = reactive({
  visible: false,
  x: 0,
  y: 0,
  xOffset: 20
});

function toggleLegend(event: MouseEvent) {
  event.preventDefault();
  const button = getElementOrAncestorWithTag(
    event.target as HTMLElement,
    "BUTTON"
  );
  legend.visible = !legend.visible;
  if (legend.visible) {
    if (button != null) {
      const rect = button.getBoundingClientRect();
      legend.x = rect.left + rect.width / 2;
      legend.y = rect.top;
      legend.xOffset = rect.width / 2;
    } else {
      legend.x = event.clientX;
      legend.y = event.clientY;
    }
  }
}
</script>

<style lang="scss">
.chart-component {
  position: relative;

  &__main {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__chart {
    contain: strict;
    .-editable > & {
      pointer-events: none;
    }
  }

  &__empty {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    text-align: center;
    align-items: center;
    justify-content: center;
    gap: 0.75em;
    font-size: 1.25rem;

    .icon {
      font-size: 2rem;
    }
  }
}
</style>